import { forwardRef, useMemo, useState } from "react";
import { Search, Plus, PackagePlus, AlertTriangle, Printer } from "lucide-react";
import { fefoAllocate, fmtExpiry, formatMoney, toMinor } from "@/lib/money";

function Tile({ p, biz, today, onAdd, i }) {
  const pharmacy = biz.business_type === "pharmacy";
  const next = pharmacy ? fefoAllocate(p.batches, 1, today).allocations[0] : null;
  const blocked = pharmacy && !next;
  return (
    <button
      onClick={() => onAdd(p)}
      data-testid={`product-tile-${p.id}`}
      style={{ animationDelay: `${Math.min(i, 24) * 18}ms` }}
      className={`tile-in group relative flex min-h-[124px] flex-col justify-between rounded-xl border bg-white p-3 text-left transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-slate-900 hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.45)] active:translate-y-0 active:scale-[0.98] sm:p-3.5 ${
        blocked ? "border-dashed border-slate-300 opacity-70" : "border-slate-200"
      }`}
    >
      <div>
        <div className="line-clamp-2 text-[13.5px] font-semibold leading-snug text-slate-900">{p.name}</div>
        {p.barcode && <div className="mt-1 font-mono text-[10px] tracking-wider text-slate-400">{p.barcode}</div>}
      </div>
      <div className="mt-2 flex items-end justify-between gap-2">
        <div className="money text-[15px] font-bold text-slate-900" data-testid={`product-price-${p.id}`}>
          <span className="mr-1 text-[10px] font-medium text-slate-400">{biz.currency}</span>
          {formatMoney(toMinor(p.price, biz.decimals), biz.decimals)}
        </div>
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-slate-900 text-amber-400 transition-colors group-hover:bg-amber-500 group-hover:text-slate-950">
          <Plus className="h-4 w-4" />
        </span>
      </div>
      {pharmacy && (
        <div
          className={`mt-2 truncate rounded-md px-1.5 py-1 font-mono text-[10px] ${blocked ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-700"}`}
          data-testid={`product-batch-hint-${p.id}`}
        >
          {blocked ? (
            <span className="inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> No valid stock</span>
          ) : (
            <>B#{next.batch_no} · {fmtExpiry(next.expiry)} · {p.valid_stock} left</>
          )}
        </div>
      )}
    </button>
  );
}

export const ProductGrid = forwardRef(function ProductGrid({ products, biz, today, onAdd, onSearchEnter, onQuickAdd, onReprintLast, cameraSlot }, searchRef) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const cats = useMemo(() => ["All", ...Array.from(new Set(products.map((p) => p.category)))], [products]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return products.filter(
      (p) => (cat === "All" || p.category === cat) && (!s || p.name.toLowerCase().includes(s) || (p.barcode || "").includes(s))
    );
  }, [products, q, cat]);

  const onKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (onSearchEnter(q.trim(), shown)) setQ("");
    } else if (e.key === "Escape") {
      setQ("");
      e.currentTarget.blur();
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="sticky top-0 z-10 space-y-3 border-b border-slate-200 bg-slate-50/90 px-3 pb-3 pt-3 backdrop-blur-md sm:px-5">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKey}
              placeholder="Search name or barcode"
              data-testid="pos-search-input"
              className="h-12 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-14 text-[15px] outline-none transition-[border-color,box-shadow] placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-amber-200"
            />
            <kbd className="absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-500 md:block">F2</kbd>
          </div>
          {cameraSlot}
          {onReprintLast && (
            <button onClick={onReprintLast} data-testid="pos-reprint-last-button" title="Reprint last receipt" className="flex h-12 min-w-[48px] items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-900">
              <Printer className="h-4 w-4" /> <span className="hidden xl:inline">Reprint last</span>
            </button>
          )}
          {onQuickAdd && <button
            onClick={() => onQuickAdd("")}
            data-testid="pos-quick-add-button"
            className="flex h-12 min-w-[48px] items-center justify-center gap-2 rounded-xl border border-slate-900 bg-white px-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-900 hover:text-white"
          >
            <PackagePlus className="h-4 w-4" /> <span className="hidden sm:inline">Quick add</span>
          </button>}
        </div>
        <div className="-mx-3 flex gap-1.5 overflow-x-auto px-3 pb-0.5 sm:-mx-5 sm:px-5" data-testid="pos-category-pills">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`category-pill-${c.toLowerCase().replace(/\s/g, "-")}`}
              className={`h-9 shrink-0 rounded-full px-3.5 text-[13px] font-semibold transition-colors ${
                cat === c ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-slate-400"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2.5 p-3 sm:grid-cols-3 sm:gap-3 sm:p-5 lg:grid-cols-4 2xl:grid-cols-5" data-testid="product-grid">
        {shown.map((p, i) => (
          <Tile key={p.id} p={p} biz={biz} today={today} onAdd={onAdd} i={i} />
        ))}
        {shown.length === 0 && (
          <div className="col-span-full rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="product-grid-empty">
            No products match “{q}”.
          </div>
        )}
      </div>
    </div>
  );
});
